import {
  Box,
  Chip,
  FormControlLabel,
  List,
  ListItem,
  Switch,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { SettingsPageLayout } from "@/features/Settings/SettingsPageLayout";
import {
  isMockConfigAvailable,
  type MockApiKey,
  useMockConfigStore,
} from "@/utils/mockConfig";

export const Route = createFileRoute("/_auth/settings/$siteId/mock-config")({
  beforeLoad: () => {
    if (!isMockConfigAvailable) {
      throw redirect({ to: ".." });
    }
  },
  component: MockConfigPage,
});

const API_LABELS: Record<MockApiKey, string> = {
  products: "Product API (SKU Store)",
  billing: "Billing API (Settings)",
  senderDomain: "Sender Domain API (Email settings)",
};

function MockApiToggle({
  apiKey,
  onToggle,
}: {
  apiKey: MockApiKey;
  onToggle: (key: MockApiKey, enabled: boolean) => void;
}) {
  const enabled = useMockConfigStore((s) => s[apiKey]);
  return (
    <ListItem disablePadding sx={{ py: 2 }}>
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <Switch
            checked={enabled}
            onChange={(_, checked) => onToggle(apiKey, checked)}
            inputProps={{ "aria-label": API_LABELS[apiKey] }}
          />
        }
        label={
          <Box>
            <Typography variant="body1">{API_LABELS[apiKey]}</Typography>
            <Typography variant="caption" color="text.secondary">
              {apiKey}
            </Typography>
          </Box>
        }
      />
    </ListItem>
  );
}

function MockConfigPage() {
  const queryClient = useQueryClient();
  const setApiMock = useMockConfigStore((s) => s.setApiMock);

  function handleToggle(key: MockApiKey, enabled: boolean) {
    setApiMock(key, enabled);
    void queryClient.invalidateQueries();
  }

  return (
    <SettingsPageLayout
      breadcrumbs={[{ label: "Admin" }]}
      title="Mock Data Config"
      titleAdornment={<Chip label="Dev only" size="small" color="warning" />}
      description="Toggle individual APIs to serve mock data instead of hitting the real backend. Changes take effect immediately and persist across page reloads."
      data-testid="settings-mock-config-page"
    >
      <List disablePadding>
        {(Object.keys(API_LABELS) as MockApiKey[]).map((key) => (
          <MockApiToggle key={key} apiKey={key} onToggle={handleToggle} />
        ))}
      </List>
    </SettingsPageLayout>
  );
}
